import "./mobile-player.css";
import { fmt } from "./player.js";
import { playbackModeLabel, playbackModeMarkup } from "./playback-icons.js";
import { animateLyricScroll } from "./lyric-scroll.js";
const escape = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
const icons = {
  down: '<path d="m6 9 6 6 6-6"/>',
  previous: '<path d="M5 5v14M19 5 7 12l12 7z"/>',
  next: '<path d="M19 5v14M5 5l12 7-12 7z"/>',
  play: '<path d="m8 5 11 7-11 7z"/>',
  pause: '<path d="M8 5v14M16 5v14"/>',
  heart:
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/>',
};
const icon = (name) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icons[name]}</svg>`;

export class MobilePlayer {
  constructor({
    scene,
    player,
    translationEnabled,
    toggleTranslation,
    toggleLyricSplit,
    isFavorite,
    toggleFavorite,
    cycleMode,
    onVisibility,
    openLibrary,
  }) {
    Object.assign(this, {
      scene,
      player,
      translationEnabled,
      toggleTranslation,
      toggleLyricSplit,
      isFavorite,
      toggleFavorite,
      cycleMode,
      onVisibility,
      openLibrary,
    });
    this.media = matchMedia("(max-width:900px), (max-aspect-ratio:6/5), (pointer:coarse) and (orientation:landscape) and (max-height:700px)");
    try { this.powerSave = localStorage.getItem("rhine:powerSave") === "true"; } catch {}
    try { this.coverStyle = localStorage.getItem("rhine:mobileCoverStyle") || "vinyl"; } catch { this.coverStyle = "vinyl"; }
    this.sceneHome = scene.container.parentNode;
    this.sceneNext = scene.container.nextSibling;
    this.tab = "cover";
    this.opened = false;
    this.manualUntil = 0;
    this.signature = "";
    this.active = -2;
    this.cancelLyricScroll = () => {};
    this.root = document.createElement("section");
    this.root.id = "mobilePlayer";
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "true");
    this.root.setAttribute("aria-label", "全屏音乐播放器");
    this.root.innerHTML = `<div class="mp-shell"><header class="mp-header"><button data-mobile="close" aria-label="收起播放器">${icon("down")}</button><div><small>RHINE / NOW PLAYING</small><span id="mpAlbum"></span></div><button data-mobile="install" class="mp-install" aria-label="添加到主屏幕说明">全屏<br>打开</button></header><div class="mp-tabs" role="tablist" aria-label="播放页面"><button role="tab" data-mobile="tab" data-tab="cover" aria-selected="true">封面</button><button role="tab" data-mobile="tab" data-tab="lyrics" aria-selected="false">歌词</button><button role="tab" data-mobile="tab" data-tab="queue" aria-selected="false">队列 <span id="mpQueueCount"></span></button></div><div class="mp-stage"><div id="mpCoverView" class="mp-cover-view"><div class="mp-scene-host" id="mpSceneHost"></div><button class="mp-quality" data-mobile="quality" aria-pressed="false">省电模式</button><div class="mp-artwork" id="mpArtwork"><span>R</span></div><p id="mpCoverHint"></p></div><div id="mpLyricsView" class="mp-lyrics-view" hidden><div class="mp-lyric-options"><button data-mobile="line">逐行</button><button data-mobile="word">逐字</button><button data-mobile="translation"></button><button data-mobile="font" aria-label="调整歌词字号">字号</button></div><p id="mpTranslationStatus" role="status"></p><div id="mpLyrics" class="mp-lyrics" aria-label="滚动歌词"></div><button data-mobile="follow" class="mp-follow" hidden>回到当前歌词</button></div><div id="mpQueueView" class="mp-queue-view" hidden><div class="mp-queue-heading"><span id="mpQueueHeading"></span><button data-mobile="library">前往曲库 ↗</button></div><ol id="mpQueue" aria-label="待播歌曲"></ol></div></div><div class="mp-track"><div><h2 id="mpTitle"></h2><p id="mpArtist"></p></div><button data-mobile="favorite" aria-label="喜欢当前歌曲" aria-pressed="false">${icon("heart")}</button></div><div class="mp-progress"><input id="mpSeek" type="range" min="0" max="1000" step="1" value="0" aria-label="全屏播放进度"><div><span id="mpElapsed">00:00</span><span id="mpDuration">00:00</span></div></div><div class="mp-controls"><button data-mobile="mode" class="mp-mode" aria-label="切换播放模式"></button><button data-mobile="previous" aria-label="上一首">${icon("previous")}</button><button data-mobile="toggle" class="mp-play" aria-label="播放">${icon("play")}</button><button data-mobile="next" aria-label="下一首">${icon("next")}</button><button data-mobile="library" class="mp-library" aria-label="返回音乐曲库">曲库<br>↗</button></div><p id="mpPlaybackStatus" class="mp-playback-status" role="status"></p><aside class="mp-install-help" hidden><button data-mobile="install-close" aria-label="关闭全屏说明">×</button><strong>从主屏幕全屏打开</strong><p>在 iPhone 的 Safari 中点“分享”，选择“添加到主屏幕”，若有“作为网页 App 打开”选项，请开启。以后点主屏幕图标即可独立打开。</p><small>首次播放仍需手动点播放。此方式不会下载整张专辑。</small></aside></div>`;
    document.body.append(this.root);
    this.q = (selector) => this.root.querySelector(selector);
    this.q(".mp-shell").insertAdjacentHTML("beforeend", '<div class="mp-landscape-lyrics" aria-label="当前歌词"></div>');
    const queue = this.q("#mpQueueView");
    this.root.append(queue);
    queue.setAttribute("role", "dialog");
    queue.setAttribute("aria-label", "当前播放队列");
    queue.insertAdjacentHTML("afterbegin", '<button class="mp-sheet-handle" data-mobile="queue-close" aria-label="收起队列"><span></span></button>');
    queue.querySelector(".mp-queue-heading").insertAdjacentHTML("beforeend", '<button data-mobile="queue-clear">清空待播</button><button data-mobile="queue-close" aria-label="关闭队列">×</button>');
    queue.insertAdjacentHTML("beforebegin", '<button class="mp-sheet-backdrop" data-mobile="queue-close" aria-label="关闭队列" hidden></button>');
    const queueButton = this.q(".mp-library");
    queueButton.dataset.mobile = "queue-open";
    queueButton.setAttribute("aria-label", "打开播放队列");
    queueButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 6h16M4 12h16M4 18h11"/></svg>';
    this.q("[data-mobile=font]").insertAdjacentHTML("beforebegin", '<button data-mobile="split" aria-label="长句自动拆分">拆句</button>');
    this.q("#mpPlaybackStatus").insertAdjacentHTML("afterend", '<div class="mp-source-notice" role="alert" hidden><span></span><button data-mobile="alternative" hidden></button></div>');
    this.q("#mpArtwork").insertAdjacentHTML("afterend", '<div id="mpVinyl" class="mp-vinyl" role="img" aria-label="旋转唱片封面"><div class="mp-vinyl-record"><img id="mpVinylImage" alt="" hidden><span class="mp-vinyl-hub"></span></div></div>');
    this.q("[data-mobile=quality]").insertAdjacentHTML("beforebegin", '<button class="mp-cover-style" data-mobile="cover-style" aria-label="切换封面样式"></button>');
    this.q("#mpTranslationStatus").insertAdjacentHTML("beforebegin", '<div class="mp-lyric-offset"><button data-mobile="offset-earlier" aria-label="歌词提前 0.2 秒">−</button><span id="mpLyricOffset">偏移 0.0 秒</span><button data-mobile="offset-later" aria-label="歌词延后 0.2 秒">＋</button><button data-mobile="offset-reset">重置</button></div>');
    this.q("#mpTitle").addEventListener("click", () => this.q(".mp-track").classList.toggle("expanded"));
    this.headerCover = document.createElement("img");
    this.headerCover.className = "mp-header-cover";
    this.headerCover.alt = "";
    this.headerCover.onerror = () => { this.headerCover.hidden = true; };
    this.q(".mp-header > div").prepend(this.headerCover);
    new MutationObserver(() => {
      if (this.opened) this.syncScene();
    }).observe(this.scene.container, { attributes: true, attributeFilter: ["data-renderer"] });
    this.root.addEventListener("click", (event) => {
      const el = event.target.closest("[data-mobile]");
      if (el && !el.disabled) this.action(el);
    });
    this.q("#mpSeek").addEventListener("input", (event) => {
      this.seeking = true;
      event.target.style.setProperty(
        "--progress",
        Number(event.target.value) / 10 + "%",
      );
      this.q("#mpElapsed").textContent = fmt(
        (Number(event.target.value) / 1000) * this.player.audio.duration,
      );
    });
    this.q("#mpSeek").addEventListener("change", (event) => {
      this.player.seek(Number(event.target.value) / 1000);
      this.seeking = false;
      this.render();
    });
    this.q("#mpSeek").addEventListener("blur", () => {
      this.seeking = false;
    });
    for (const name of ["pointerdown", "touchstart", "wheel"])
      this.q("#mpLyrics").addEventListener(
        name,
        () => {
          this.cancelLyricScroll();
          this.manualUntil = Infinity;
          this.q("[data-mobile=follow]").hidden = false;
        },
        { passive: true },
      );
    this.root.addEventListener("keydown", (event) => this.keyboard(event));
    let down;
    this.q(".mp-header").addEventListener("pointerdown", (event) => {
      if (!event.target.closest("button")) {
        down = { x: event.clientX, y: event.clientY };
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    });
    this.q(".mp-header").addEventListener("pointerup", (event) => {
      if (
        down &&
        event.clientY - down.y > 65 &&
        Math.abs(event.clientX - down.x) < 60
      )
        this.close();
      down = null;
    });
    this.q(".mp-header").addEventListener("pointercancel", () => {
      down = null;
    });
    this.media.addEventListener("change", () => {
      if (!this.media.matches) this.close();
    });
    player.addEventListener("change", () => this.render());
    document.addEventListener("visibilitychange", () => this.animate());
  }
  open(tab = "cover") {
    if (!this.media.matches) return false;
    this.previousFocus = document.activeElement;
    this.opened = true;
    this.root.hidden = false;
    document.body.classList.add("mobile-player-open");
    document.getElementById("hud").inert = true;
    this.setTab(tab);
    this.syncScene();
    this.render();
    this.q("[data-mobile=close]").focus({ preventScroll: true });
    return true;
  }
  close() {
    if (!this.opened) return;
    this.cancelLyricScroll();
    this.setQueue(false);
    this.opened = false;
    this.root.hidden = true;
    document.body.classList.remove("mobile-player-open");
    document.getElementById("hud").inert = false;
    this.sceneHome.insertBefore(this.scene.container, this.sceneNext);
    this.scene.mobileStage = false;
    this.scene.obscured = false;
    this.scene.cameraAim = null;
    this.onVisibility(false);
    this.scene.resize();
    cancelAnimationFrame(this.raf);
    this.raf = null;
    if (this.previousFocus?.isConnected)
      this.previousFocus.focus({ preventScroll: true });
  }
  setQueue(open) {
    this.queueOpen = open;
    this.q("#mpQueueView").hidden = !open;
    this.q(".mp-sheet-backdrop").hidden = !open;
    this.q(".mp-shell").inert = open;
    if (open) {
      this.render();
      this.q(".mp-sheet-handle").focus({ preventScroll: true });
    } else this.q(".mp-library").focus({ preventScroll: true });
  }
  setTab(tab) {
    if (tab === "queue") { this.setQueue(true); return; }
    this.setQueue(false);
    this.cancelLyricScroll();
    this.tab = tab;
    this.root.dataset.tab = tab;
    this.syncScene();
    this.manualUntil = 0;
    this.active = -2;
    for (const name of ["cover", "lyrics", "queue"])
      this.q("#mp" + name[0].toUpperCase() + name.slice(1) + "View").hidden =
        name !== tab;
    for (const el of this.root.querySelectorAll("[role=tab]"))
      el.setAttribute("aria-selected", String(el.dataset.tab === tab));
    this.render();
    this.animate();
  }
  syncScene() {
    if (!this.opened) return;
    const active = this.tab === "cover" && !this.powerSave && this.coverStyle === "case";
    this.root.dataset.powerSave = String(Boolean(this.powerSave));
    this.root.dataset.coverStyle = this.coverStyle;
    this.q("[data-mobile=cover-style]").textContent = this.coverStyle === "vinyl" ? "切换 3D 盒体" : "切换旋转唱片";
    this.q("[data-mobile=quality]").setAttribute("aria-pressed", String(Boolean(this.powerSave)));
    this.q("#mpVinyl").hidden = this.powerSave || this.coverStyle !== "vinyl";
    this.q("#mpArtwork").hidden = !this.powerSave && (this.coverStyle === "vinyl" || this.scene.container.dataset.renderer !== "fallback");
    this.q("#mpSceneHost").append(this.scene.container);
    this.scene.mobileStage = true;
    this.scene.obscured = !active;
    if (active) {
      this.scene.setDetail(true);
      requestAnimationFrame(() => this.scene.resize());
    }
  }
  action(el) {
    const p = this.player;
    switch (el.dataset.mobile) {
      case "queue-open": this.setQueue(true); return;
      case "queue-close": this.setQueue(false); return;
      case "queue-clear": p.clearPending(); break;
      case "quality":
        this.powerSave = !this.powerSave;
        try { localStorage.setItem("rhine:powerSave", String(this.powerSave)); } catch {}
        this.syncScene();
        break;
      case "cover-style":
        this.coverStyle = this.coverStyle === "vinyl" ? "case" : "vinyl";
        try { localStorage.setItem("rhine:mobileCoverStyle", this.coverStyle); } catch {}
        this.syncScene();
        break;
      case "font":
        this.largeLyrics = !this.largeLyrics;
        this.root.classList.toggle("large-lyrics", this.largeLyrics);
        break;
      case "offset-earlier":
        p.adjustLyricOffset(-0.2);
        break;
      case "offset-later":
        p.adjustLyricOffset(0.2);
        break;
      case "offset-reset":
        p.adjustLyricOffset(-p.lyricOffset);
        break;
      case "queue-remove":
        p.remove(Number(el.dataset.index));
        break;
      case "queue-up":
        p.move(Number(el.dataset.index), Number(el.dataset.index) - 1);
        break;
      case "close":
        this.close();
        return;
      case "tab":
        this.setTab(el.dataset.tab);
        return;
      case "toggle":
        p.toggle();
        break;
      case "alternative":
        p.playAlternative();
        break;
      case "previous":
        p.previous();
        break;
      case "next":
        p.next();
        break;
      case "mode":
        this.cycleMode();
        break;
      case "favorite":
        if (p.current) this.toggleFavorite(p.current);
        break;
      case "line":
        p.setLyricMode("line");
        break;
      case "word":
        p.setLyricMode("word");
        break;
      case "translation":
        this.toggleTranslation();
        break;
      case "split":
        this.toggleLyricSplit();
        break;
      case "retry":
        p.ensureTranslations(true);
        break;
      case "follow":
        this.manualUntil = 0;
        this.active = -2;
        break;
      case "seek-line": {
        const line = p.lyrics[Number(el.dataset.index)];
        if (line && Number.isFinite(p.audio.duration))
          p.audio.currentTime = Math.max(0, line.time + p.lyricOffset);
        this.manualUntil = 0;
        this.active = -2;
        break;
      }
      case "queue-play":
        p.play(
          p.queue[Number(el.dataset.index)],
          undefined,
          Number(el.dataset.index),
        );
        break;
      case "library":
        this.close();
        this.openLibrary();
        return;
      case "install":
        this.q(".mp-install-help").hidden = false;
        return;
      case "install-close":
        this.q(".mp-install-help").hidden = true;
        return;
    }
    this.render();
  }
  keyboard(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      if (!this.q(".mp-install-help").hidden)
        this.q(".mp-install-help").hidden = true;
      else if (this.queueOpen) this.setQueue(false);
      else this.close();
    }
    if (event.key === "Tab") {
      const list = [
        ...this.root.querySelectorAll(
          "button:not(:disabled),input:not(:disabled)",
        ),
      ].filter((el) => el.getClientRects().length && !el.closest("[inert]"));
      const first = list[0],
        last = list.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  }
  render() {
    if (!this.opened) return;
    const p = this.player,
      song = p.current,
      audio = p.audio;
    if (song && this.scene.song?.key !== song.key) {
      this.scene.select(song, p.index, p.queue.length);
      this.scene.prefetchCovers(p.queue, p.index);
      this.scene.setDetail(true);
    }
    this.q("#mpTitle").title = song?.title || "";
    this.q("#mpAlbum").title = song?.album || "";
    this.headerCover.hidden = this.tab !== "lyrics" || !song?.cover;
    if (song?.cover && this.headerCover.getAttribute("src") !== song.cover) this.headerCover.src = song.cover;
    this.q("#mpAlbum").textContent = song?.album || "我的音乐";
    this.q("#mpTitle").textContent = song?.title || "选择歌曲开始播放";
    this.q("#mpArtist").textContent = song
      ? song.artist + (p.playbackFormat ? " · " + p.playbackFormat : "")
      : "从曲库选择一首歌曲";
    this.q("#mpCoverHint").textContent = song?.preview
      ? "当前为音源提供的试听片段"
      : song?.album || "RHINE MUSIC DATABASE";
    this.root.dataset.playing = String(Boolean(song && !audio.paused && !p.loading));
    const vinylImage = this.q("#mpVinylImage");
    if (vinylImage.dataset.cover !== (song?.cover || "")) {
      vinylImage.dataset.cover = song?.cover || "";
      vinylImage.hidden = !song?.cover;
      if (song?.cover) { vinylImage.src = song.cover; this.updatePalette(song.cover); }
    }
    const artwork = this.q("#mpArtwork");
    if (artwork.dataset.cover !== (song?.cover || "")) {
      artwork.dataset.cover = song?.cover || "";
      artwork.replaceChildren();
      if (song?.cover) {
        const img = document.createElement("img");
        img.src = song.cover;
        img.alt = "专辑封面";
        img.onerror = () => {
          img.remove();
          artwork.textContent = "R";
        };
        artwork.append(img);
      } else artwork.textContent = "R";
    }
    const toggle = this.q("[data-mobile=toggle]"),
      toggleState = p.loading ? "loading" : audio.paused ? "play" : "pause";
    if (toggle.dataset.state !== toggleState) {
      toggle.dataset.state = toggleState;
      toggle.innerHTML = p.loading
        ? '<span class="mp-loading">···</span>'
        : icon(toggleState);
    }
    toggle.setAttribute("aria-label", audio.paused ? "播放" : "暂停");
    toggle.setAttribute("aria-busy", String(p.loading));
    toggle.disabled = !song;
    for (const name of ["previous", "next", "favorite"])
      this.q("[data-mobile=" + name + "]").disabled = !song;
    this.q("[data-mobile=favorite]").setAttribute(
      "aria-pressed",
      String(this.isFavorite(song?.key)),
    );
    const modeButton = this.q("[data-mobile=mode]");
    if (modeButton.dataset.mode !== p.mode) {
      modeButton.dataset.mode = p.mode;
      modeButton.innerHTML = playbackModeMarkup(p.mode);
    }
    modeButton.setAttribute("aria-label", playbackModeLabel(p.mode) + "，点击切换");
    this.q(".mp-install").hidden = Boolean(
      navigator.standalone || matchMedia("(display-mode:standalone)").matches,
    );
    this.q("#mpPlaybackStatus").textContent = p.failed
      ? p.sourceState?.message || "音源播放失败，请点播放重试"
      : p.loading
        ? "正在载入音源…"
        : p.buffering
          ? "正在缓冲…"
          : song?.preview
            ? "试听播放 · " + fmt(audio.duration)
            : "";
    const sourceNotice = this.q(".mp-source-notice");
    sourceNotice.hidden = !p.failed || !p.sourceState;
    if (!sourceNotice.hidden) {
      sourceNotice.querySelector("span").textContent = p.sourceState.message;
      const alternative = p.sourceState.alternative;
      const button = sourceNotice.querySelector("button");
      button.hidden = !alternative;
      button.textContent = alternative ? `播放推荐版本：${alternative.title} ↗` : "";
    }
    const seek = this.q("#mpSeek");
    seek.disabled = !(Number.isFinite(audio.duration) && audio.duration > 0);
    if (!this.seeking) {
      seek.value = seek.disabled
        ? 0
        : (audio.currentTime / audio.duration) * 1000;
      seek.style.setProperty("--progress", Number(seek.value) / 10 + "%");
      this.q("#mpElapsed").textContent = fmt(audio.currentTime);
    }
    this.q("#mpDuration").textContent = fmt(audio.duration);
    this.updateLandscapeLyrics();
    this.q("#mpQueueCount").textContent = p.queue.length || "";
    if (this.queueOpen) {
      const key =
        p.queue.map((s, i) => i + ":" + s.key).join("|") + "|" + p.index;
      this.q("#mpQueueHeading").textContent =
        "正在播放 " + (p.index + 1) + " / " + p.queue.length;
      if (key !== this.queueKey) {
        this.queueKey = key;
        this.q("#mpQueue").innerHTML = p.queue
          .map(
            (s, i) =>
              `<li><button data-mobile="queue-play" data-index="${i}" aria-current="${i === p.index ? "true" : "false"}"><span>${String(i + 1).padStart(2, "0")}</span><div><strong>${escape(s.title)}</strong><small>${escape(s.artist)}</small></div><span>${i === p.index ? "♫" : "↗"}</span></button><div class="mp-queue-actions"><button data-mobile="queue-up" data-index="${i}" aria-label="上移 ${escape(s.title)}" ${i === 0 ? "disabled" : ""}>↑</button><button data-mobile="queue-remove" data-index="${i}" aria-label="移出 ${escape(s.title)}" ${i === p.index ? "disabled" : ""}>×</button></div></li>`,
          )
          .join("");
      }
    }
    if (this.tab === "lyrics") this.renderLyrics();
    this.animate();
  }
  renderLyrics() {
    const p = this.player;
    this.q("#mpLyricOffset").textContent = `偏移 ${p.lyricOffset.toFixed(1)} 秒`;
    this.q("[data-mobile=line]").setAttribute(
      "aria-pressed",
      String(p.lyricMode === "line"),
    );
    this.q("[data-mobile=word]").setAttribute(
      "aria-pressed",
      String(p.lyricMode === "word"),
    );
    this.q("[data-mobile=word]").disabled = !p.wordLyrics.length;
    this.q("[data-mobile=split]").setAttribute("aria-pressed", String(p.splitLongLyrics));
    this.q("[data-mobile=translation]").textContent =
      "译文 " + (this.translationEnabled() ? "开" : "关");
    const status =
      p.translationState === "pending"
        ? "正在获取译文…"
        : p.translationState === "error"
          ? p.translationError
          : p.translationState === "original"
            ? "中文原文"
            : p.translationProvider;
    this.q("#mpTranslationStatus").innerHTML = this.translationEnabled()
      ? escape(status) +
        (p.translationState === "error"
          ? ' <button data-mobile="retry">重试</button>'
          : "")
      : "";
    const key = [
      p.current?.key,
      p.lyrics.length,
      p.lyricMode,
      p.splitLongLyrics,
      this.translationEnabled(),
      p.translations.length,
    ].join("|");
    if (this.signature !== key) {
      this.cancelLyricScroll();
      const oldTop = this.q("#mpLyrics").scrollTop;
      this.signature = key;
      this.q("#mpLyrics").innerHTML = p.lyrics.length
        ? p.lyrics
            .map(
              (line, i) =>
                `<button data-mobile="seek-line" data-index="${i}"><span>${p.lyricMode === "word" && line.words ? line.words.map((w) => `<span class="mp-word">${escape(w.text)}</span>`).join("") : escape(line.text || "♪")}</span>${this.translationEnabled() && line.translation ? `<small>${escape(line.translation)}</small>` : ""}</button>`,
            )
            .join("")
        : '<p class="mp-empty">' +
          (p.loading ? "正在读取歌词…" : "这首歌曲暂时没有歌词") +
          "</p>";
      this.rows = [
        ...this.q("#mpLyrics").querySelectorAll("[data-mobile=seek-line]"),
      ];
      this.active = -2;
      if (this.manualUntil > Date.now()) this.q("#mpLyrics").scrollTop = oldTop;
    }
    this.highlight();
  }
  highlight() {
    const p = this.player,
      box = this.q("#mpLyrics");
    let active = -1;
    for (let i = 0; i < p.lyrics.length; i++) {
      if (p.lyrics[i].time + p.lyricOffset <= p.audio.currentTime) active = i;
      else break;
    }
    if (this.manualUntil && Date.now() > this.manualUntil) {
      this.manualUntil = 0;
      this.active = -2;
    }
    this.q("[data-mobile=follow]").hidden = !this.manualUntil;
    if (active !== this.active) {
      const previous = this.active;
      this.active = active;
      if (previous < 0) this.q("#mpLyrics button.active")?.classList.remove("active");
      else this.rows?.[previous]?.classList.remove("active");
      this.rows?.[active]?.classList.add("active");
      this.wordNodes = this.rows?.[active]?.querySelectorAll(".mp-word");
      const row = this.rows?.[active];
      if (row && !this.manualUntil) {
        this.cancelLyricScroll();
        this.cancelLyricScroll = animateLyricScroll(box,
          row.offsetTop - box.offsetTop - box.clientHeight * 0.34 + row.clientHeight / 2,
          { reduced: matchMedia("(prefers-reduced-motion:reduce)").matches,
            interrupted: () => Boolean(this.manualUntil) || !this.opened || this.tab !== "lyrics" });
      }
    }
    const words = p.lyrics[active]?.words;
    if (words && this.wordNodes)
      for (let i = 0; i < words.length; i++) {
        const word = words[i],
          end = word.end ?? words[i + 1]?.time ?? word.time + 0.5;
        this.wordNodes[i]?.style.setProperty(
          "--word-progress",
          Math.min(
            100,
            Math.max(
              0,
              ((p.audio.currentTime - word.time) /
                Math.max(0.05, end - word.time)) *
                100,
            ),
          ) + "%",
        );
      }
  }
  updatePalette(cover) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (this.player.current?.cover !== cover) return;
      try {
        const canvas = document.createElement("canvas"); canvas.width = canvas.height = 16;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, 16, 16);
        const pixels = ctx.getImageData(0, 0, 16, 16).data;
        let r=0,g=0,b=0;
        for(let i=0;i<pixels.length;i+=4){r+=pixels[i];g+=pixels[i+1];b+=pixels[i+2];}
        this.root.style.setProperty("--art-color", `rgb(${Math.round(r/256)} ${Math.round(g/256)} ${Math.round(b/256)})`);
      } catch { this.root.style.removeProperty("--art-color"); }
    };
    img.src = cover;
  }
  updateLandscapeLyrics() {
    const p=this.player;
    let index=0;
    for(let i=0;i<p.lyrics.length;i++) if(p.lyrics[i].time+p.lyricOffset<=p.audio.currentTime) index=i;
    const lines=p.lyrics.slice(index,index+3);
    const html=lines.map((line,i)=>`<p class="${i===0?"current":""}">${escape(line.text || "♪")}</p>`).join("");
    const target=this.q(".mp-landscape-lyrics");
    if(target.innerHTML!==html) target.innerHTML=html;
  }
  animate() {
    const shouldRun =
      this.opened &&
      this.tab === "lyrics" &&
      !this.player.audio.paused &&
      !document.hidden;
    if (!shouldRun) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
      return;
    }
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = null;
      this.highlight();
      this.animate();
    });
  }
}
